/* eslint-disable @next/next/no-img-element */
'use client';
import { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import type { Dictionary, Locale } from '@/app/[lang]/dictionaries';

const locales: Locale[] = ['vi', 'en'];
const localeLabels: Record<Locale, string> = { vi: 'VI', en: 'EN' };
const LOCALE_COOKIE = 'NEXT_LOCALE';
const LOCALE_COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

function stripLocale(pathname: string, lang: Locale) {
  const withoutLocale = pathname.replace(new RegExp(`^/${lang}`), '');
  return withoutLocale === '' ? '/' : withoutLocale;
}

function rememberLocale(locale: Locale) {
  document.cookie = `${LOCALE_COOKIE}=${locale}; path=/; max-age=${LOCALE_COOKIE_MAX_AGE}; SameSite=Lax`;
}

export default function SiteNav({ lang, dict }: { lang: Locale; dict: Dictionary['nav'] }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const pathname = usePathname();
  const currentPath = stripLocale(pathname, lang);

  const homeHref = `/${lang}`;
  const retreatHref = `/${lang}/retreat`;

  return (
    <nav className="fixed top-0 left-0 right-0 w-full bg-[rgba(2,44,34,0.8)] backdrop-blur-md px-4 sm:px-6 lg:px-10 py-6 z-50 shadow-[0_25px_50px_-12px_rgba(2,44,34,0.2)]">
      <div className="flex items-center justify-between">
        {/* Logo */}
        <div className="flex items-center">
          <Link href={homeHref}>
            <img
              src="/Image/icon.png"
              alt={dict.logoAlt}
              className="h-8 sm:h-10 w-auto"
            />
          </Link>
        </div>

        {/* Desktop Nav Links */}
        <div className="hidden md:flex items-center gap-6 lg:gap-12">
          <Link
            href={`${homeHref}#nhan-invo`}
            className={`${currentPath === '/' ? 'text-[#4ade80]' : 'text-[#e2e3df] hover:text-white'} text-sm font-normal transition-colors font-['Plus_Jakarta_Sans']`}
          >
            {dict.ringLink}
          </Link>
          <Link
            href={retreatHref}
            className={`${currentPath === '/retreat' ? 'text-[#4ade80]' : 'text-[#e2e3df] hover:text-white'} text-sm font-normal transition-colors font-['Plus_Jakarta_Sans'] max-w-[200px] lg:max-w-none text-center lg:text-left`}
          >
            {dict.retreatLink}
          </Link>
        </div>

        {/* Desktop CTA + Language Switcher + Mobile Hamburger */}
        <div className="flex items-center gap-3">
          <div className="hidden md:flex items-center rounded-full border border-[#2d4f3c] overflow-hidden text-xs font-semibold font-['Plus_Jakarta_Sans']">
            {locales.map((locale) => (
              <Link
                key={locale}
                href={`/${locale}${currentPath === '/' ? '' : currentPath}`}
                onClick={() => rememberLocale(locale)}
                className={`px-3 py-1.5 transition-colors ${locale === lang ? 'bg-[#4ade80] text-[#0d2b1e]' : 'text-[#e2e3df] hover:text-white'}`}
              >
                {localeLabels[locale]}
              </Link>
            ))}
          </div>
          <button className="hidden md:flex items-center justify-center px-[32px] py-[10px] rounded-full bg-[linear-gradient(103.73deg,#A9CFB7_0%,#2D4F3C_100%)] shadow-[0px_10px_15px_-3px_rgba(169,207,183,0.1),0px_4px_6px_-4px_rgba(169,207,183,0.1)] text-[#E2E3DF] text-sm font-semibold font-['Plus_Jakarta_Sans'] hover:opacity-90 transition-opacity">
            {dict.cta}
          </button>
          {/* Hamburger */}
          <button
            className="md:hidden flex flex-col justify-center items-center w-9 h-9 gap-1.5"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={dict.toggleMenu}
          >
            <span className={`block w-6 h-0.5 bg-[#e2e3df] transition-all duration-300${mobileMenuOpen ? ' rotate-45 translate-y-2' : ''}`} />
            <span className={`block w-6 h-0.5 bg-[#e2e3df] transition-all duration-300${mobileMenuOpen ? ' opacity-0' : ''}`} />
            <span className={`block w-6 h-0.5 bg-[#e2e3df] transition-all duration-300${mobileMenuOpen ? ' -rotate-45 -translate-y-2' : ''}`} />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden absolute top-full left-0 w-full bg-[rgba(2,44,34,0.95)] backdrop-blur-md border-t border-[#1a3d27] px-4 py-5 flex flex-col gap-4 shadow-xl">
          <Link
            href={`${homeHref}#nhan-invo`}
            className={`${currentPath === '/' ? 'text-[#4ade80]' : 'text-[#e2e3df] hover:text-white'} text-sm font-normal transition-colors font-['Plus_Jakarta_Sans'] py-2`}
            onClick={() => setMobileMenuOpen(false)}
          >
            {dict.ringLink}
          </Link>
          <Link
            href={retreatHref}
            className={`${currentPath === '/retreat' ? 'text-[#4ade80]' : 'text-[#e2e3df] hover:text-white'} text-sm font-normal transition-colors font-['Plus_Jakarta_Sans'] py-2`}
            onClick={() => setMobileMenuOpen(false)}
          >
            {dict.retreatLink}
          </Link>
          <div className="flex items-center gap-2 py-2">
            {locales.map((locale) => (
              <Link
                key={locale}
                href={`/${locale}${currentPath === '/' ? '' : currentPath}`}
                onClick={() => {
                  rememberLocale(locale);
                  setMobileMenuOpen(false);
                }}
                className={`px-3 py-1.5 rounded-full border border-[#2d4f3c] text-xs font-semibold font-['Plus_Jakarta_Sans'] transition-colors ${locale === lang ? 'bg-[#4ade80] text-[#0d2b1e]' : 'text-[#e2e3df] hover:text-white'}`}
              >
                {localeLabels[locale]}
              </Link>
            ))}
          </div>
          <button className="w-full flex items-center justify-center px-[32px] py-[10px] rounded-full bg-[linear-gradient(103.73deg,#A9CFB7_0%,#2D4F3C_100%)] shadow-[0px_10px_15px_-3px_rgba(169,207,183,0.1),0px_4px_6px_-4px_rgba(169,207,183,0.1)] text-[#E2E3DF] text-sm font-semibold font-['Plus_Jakarta_Sans'] mt-2 hover:opacity-90 transition-opacity">
            {dict.cta}
          </button>
        </div>
      )}
    </nav>
  );
}
